import type { SearchResponse } from '@algolia/client-search';

import { createAlgoliaClient, getAlgoliaPersistentProductFilters } from '@libs/algolia';
import { type Locale, logger } from '@libs/core';

import { MAX_HITS_PER_PAGE } from '../constants';
import type { ProductHit } from '../types';
import { AlgoliaIndice, localePrefix } from '../types';
import type { ReqOptions } from '../types/reqOptions';
import { getAlgoliaRefererHeader } from './getAlgoliaRefererHeader';
import { getSearchIndex } from './getSearchIndex';

/**
 * @deprecated Prefer the CWEA HTTP product lookup endpoints (DP-2248) instead of direct Algolia access.
 */
export const getObjectById = async <TObject>(id: string, indexName: string, requestOptions?: ReqOptions) => {
    try {
        const index = getSearchIndex(indexName);

        return await index.getObject<TObject>(id, {
            ...requestOptions,
            headers: {
                ...requestOptions?.headers,
                'x-petm-algolia-caller': 'web:algolia-lib:getObjectById',
                'x-petm-algolia-referer': getAlgoliaRefererHeader(),
            },
        });
    } catch (error) {
        logger.error('[algolia/getObjectById]', { index: indexName }, 'error %o', error);

        return undefined;
    }
};

/**
 * @deprecated Use the CWEA HTTP endpoint `GET /product/:id` instead.
 */
export const getProductById = async (
    id: string,
    caller?: string,
    callerFunction?: string
): Promise<ProductHit | undefined> => {
    try {
        const index = getSearchIndex(AlgoliaIndice.PRODUCTS);
        const masterIdFilter = `masterProductID:${id}`;

        const persistentFilters = getAlgoliaPersistentProductFilters();
        const response = await index.search<ProductHit>('', {
            filters: `(${persistentFilters}) AND (${masterIdFilter} OR objectID:${id})`,
            headers: {
                'x-petm-algolia-caller': `web:${caller}:getProductById-${callerFunction}`,
                'x-petm-algolia-referer': getAlgoliaRefererHeader(),
            },
        });

        if (response?.hits.length > 0) {
            return response.hits[0];
        }

        return undefined;
    } catch (error) {
        console.error('[algolia/getProductById]', { index: AlgoliaIndice.PRODUCTS }, JSON.stringify(error));

        return undefined;
    }
};

/**
 * @deprecated Prefer the CWEA HTTP product lookup endpoints (DP-2248) instead of direct Algolia access.
 */
export const getMultipleObjectsById = async <TObject>(
    ids: string[],
    indexName: string,
    requestOptions?: ReqOptions
) => {
    try {
        const index = getSearchIndex(indexName);

        return (
            await index.getObjects<TObject>(ids, {
                ...requestOptions,
                headers: {
                    ...requestOptions?.headers,
                    'x-petm-algolia-caller': 'web:algolia-lib:getMultipleObjectsById',
                    'x-petm-algolia-referer': getAlgoliaRefererHeader(),
                },
            })
        ).results;
    } catch (error) {
        console.error('[algolia/getMultipleObjectsById]', { index: indexName }, JSON.stringify(error));

        return [];
    }
};

/**
 * Retrieves all product variants from Algolia search index.
 *
 * Supports two query types:
 * - **Web Style ID**: Returns all variants of a master product, with the best-selling variant first
 * - **SKU**: Returns all variants of the product, with the matching SKU variant first
 *
 * @deprecated Use the CWEA HTTP endpoint `GET /product/variants` instead.
 *
 * @param id - The product identifier (Web Style ID or SKU)
 * @param locale - The locale for selecting the appropriate Algolia index (en-US or en-CA)
 * @returns Array of product variants, or empty array if no matches found
 *
 * @example
 * ```typescript
 * // Query by Web Style ID with US locale
 * const variants = await getAllVariantsById('WS12345', Locale['en-US']);
 * // Returns: [bestSellerVariant, ...otherVariants]
 *
 * // Query by SKU with CA locale
 * const variants = await getAllVariantsById('SKU-ABC-123', Locale['en-CA']);
 * // Returns: [matchingSKUVariant, ...otherVariants]
 * ```
 */
export const getAllVariantsById = async (id: string, locale: Locale): Promise<ProductHit[] | undefined> => {
    const prefix = localePrefix[locale];
    const localeSpecificIndex = `r-${prefix}_products_best-sellers`;

    try {
        const client = createAlgoliaClient();

        const webStyleIdFilter = `masterProductID:${id}`;
        const skuFilter = `objectID:${id}`;

        const persistentFilters = getAlgoliaPersistentProductFilters();
        const response = (await client.multipleQueries<ProductHit>(
            [
                {
                    indexName: localeSpecificIndex,
                    query: '',
                    params: {
                        filters: `(${persistentFilters}) AND ${webStyleIdFilter}`,
                        distinct: 0,
                        hitsPerPage: MAX_HITS_PER_PAGE,
                        attributesToRetrieve: ['*', '-inventory'],
                    },
                },
                {
                    indexName: localeSpecificIndex,
                    query: '',
                    params: {
                        filters: `(${persistentFilters}) AND ${skuFilter}`,
                        attributesToRetrieve: ['*', '-inventory'],
                    },
                },
            ],
            {
                headers: {
                    'x-petm-algolia-caller': `web:algolia-lib:getAllVariantsById-multipleQueries`,
                    'x-petm-algolia-referer': getAlgoliaRefererHeader(),
                },
            }
        )) as {
            results: SearchResponse<ProductHit>[];
        };

        const [resultsByWebStyleId, resultsBySku] = response.results;

        if (resultsByWebStyleId.hits.length > 0) {
            return resultsByWebStyleId.hits;
        }

        if (resultsBySku.hits.length > 0 && resultsBySku.hits[0]) {
            const matchingSKUVariant = resultsBySku.hits[0];
            const matchedWebStyleIdFilter = `masterProductID:${matchingSKUVariant.masterProductID}`;

            const allOtherVariantsResponse = await client.initIndex(localeSpecificIndex).search<ProductHit>('', {
                filters: `${persistentFilters} AND ${matchedWebStyleIdFilter} AND NOT ${skuFilter}`,
                distinct: 0,
                hitsPerPage: MAX_HITS_PER_PAGE,
                attributesToRetrieve: ['*', '-inventory'],
                headers: {
                    'x-petm-algolia-caller': `web:algolia-lib:getAllVariantsById-allOtherVariants`,
                    'x-petm-algolia-referer': getAlgoliaRefererHeader(),
                },
            });

            return [matchingSKUVariant, ...allOtherVariantsResponse.hits];
        }

        return [];
    } catch (error) {
        console.error('[algolia/getAllVariantsById]', { index: localeSpecificIndex, locale }, JSON.stringify(error));

        return undefined;
    }
};
